import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Spinner,
  Alert,
  AlertIcon,
  Button,
  Text,
  HStack,
  VStack,
  Card,
  CardBody,
  Link,
} from '@chakra-ui/react';
import { ExternalLinkIcon, InfoIcon } from '@chakra-ui/icons';
import { callApi } from '../callApi';
import HeaderMenus from './HeaderMenus';
import { AppContext } from './AppContext';

const NMAStrictDashboard = () => {
  const { accessToken } = useContext(AppContext);
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCases = async () => {
      if (!accessToken) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const response = await callApi('/nma/cases', accessToken, {
          method: 'GET'
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.status === 'success') {
            setCases(data.cases || []);
          } else {
            setError(data.message || 'Failed to fetch cases.');
          }
        } else {
          const errorData = await response.json().catch(() => ({}));
          setError(errorData.message || `Server error: ${response.status}`);
        }
      } catch (err) {
        console.error('Error fetching NMA cases:', err);
        setError('An error occurred while fetching cases.');
      } finally {
        setLoading(false);
      }
    };

    fetchCases();
  }, [accessToken]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getCoverageBadgeColor = (basis) => {
    if (!basis) return 'gray';
    if (basis.includes('NCD')) return 'blue';
    if (basis.includes('LCD')) return 'teal';
    if (basis.includes('Article')) return 'cyan';
    if (basis.includes('Commercial')) return 'purple';
    if (basis.includes('None Found')) return 'red';
    return 'gray';
  };

  return (
    <Box minH="100vh" bg="gray.50">
      <HeaderMenus />
      <Box maxW="container.xl" mx="auto" py={8} px={4}>
        <VStack spacing={6} align="stretch">
          <HStack justify="space-between">
            <VStack align="start" spacing={1}>
              <Heading size="lg" color="brand.700">NMA Strict Medical Necessity Dashboard</Heading>
              <Text color="gray.600">Overview of all cases processed through the strict IONM pipeline.</Text>
            </VStack>
            <Button
              as={RouterLink}
              to="/nma-guidelines"
              colorScheme="blue"
              variant="outline"
              size="sm"
              leftIcon={<InfoIcon />}
            >
              View Coding Guidelines
            </Button>
          </HStack>

          {error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              {error}
            </Alert>
          )}

          <Card shadow="sm" borderRadius="lg">
            <CardBody>
              {loading ? (
                <VStack py={10}>
                  <Spinner size="xl" color="brand.500" />
                  <Text mt={4} color="gray.500">Loading cases...</Text>
                </VStack>
              ) : cases.length === 0 ? (
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  No NMA strict cases found.
                </Alert>
              ) : (
                <Box overflowX="auto">
                  <Table variant="simple" size="md">
                    <Thead bg="gray.50">
                      <Tr>
                        <Th>External Case ID</Th>
                        <Th>Claim Type</Th>
                        <Th>Service Date</Th>
                        <Th>Coverage Basis</Th>
                        <Th>MAC Jurisdiction</Th>
                        <Th>Legacy ICD-10</Th>
                        <Th>Strict ICD-10</Th>
                        <Th isNumeric>Warnings</Th>
                        <Th>Processed At</Th>
                        <Th>Action</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {cases.map((c, index) => (
                        <Tr key={`${c.internal_case_id}-${c.claim_type}-${index}`} _hover={{ bg: 'gray.50' }}>
                          <Td fontWeight="medium">
                            <Link 
                              href={`/ionm-strict-analysis/${c.external_case_id}`} 
                              isExternal 
                              color="brand.800" 
                              fontWeight="bold"
                            >
                              {c.external_case_id} <ExternalLinkIcon mx="2px" />
                            </Link>
                          </Td>
                          <Td>
                            <Badge colorScheme={c.claim_type === 'pro' ? 'blue' : 'purple'}>
                              {c.claim_type?.toUpperCase() || 'PRO'}
                            </Badge>
                          </Td>
                          <Td>{c.service_date || 'N/A'}</Td>
                          <Td>
                            <Badge colorScheme={getCoverageBadgeColor(c.coverage_basis)}>
                              {c.coverage_basis || 'N/A'}
                            </Badge>
                          </Td>
                          <Td>
                            <Text noOfLines={1} maxW="200px" title={c.mac_jurisdiction}>
                              {c.mac_jurisdiction || 'N/A'}
                            </Text>
                          </Td>
                          <Td>
                            <HStack wrap="wrap" spacing={1}>
                              {c.legacy_codes && c.legacy_codes.length > 0 ? (
                                c.legacy_codes.map((code, idx) => (
                                  <Badge key={idx} colorScheme="gray" variant="subtle">
                                    {code}
                                  </Badge>
                                ))
                              ) : (
                                <Text fontSize="xs" color="gray.400">None</Text>
                              )}
                            </HStack>
                          </Td>
                          <Td>
                            <HStack wrap="wrap" spacing={1}>
                              {c.strict_codes && c.strict_codes.length > 0 ? (
                                c.strict_codes.map((code, idx) => {
                                  // Highlight codes that are in strict but NOT in legacy (newly added)
                                  // Or just show them as green if they survived the strict filter
                                  const isKept = c.legacy_codes?.includes(code);
                                  return (
                                    <Badge key={idx} colorScheme={isKept ? "green" : "purple"} variant="solid">
                                      {code}
                                    </Badge>
                                  );
                                })
                              ) : (
                                <Text fontSize="xs" color="red.400">All Dropped</Text>
                              )}
                            </HStack>
                          </Td>
                          <Td isNumeric>
                            {c.warnings_count > 0 ? (
                              <Badge colorScheme="orange">{c.warnings_count}</Badge>
                            ) : (
                              <Text color="gray.500">0</Text>
                            )}
                          </Td>
                          <Td fontSize="sm" color="gray.600">{formatDate(c.created_at)}</Td>
                          <Td>
                            <Button
                              size="sm"
                              colorScheme="brand"
                              variant="outline"
                              rightIcon={<ExternalLinkIcon />}
                              onClick={() => window.open(`/ionm-strict-analysis/${c.external_case_id}`, '_blank')}
                            >
                              View
                            </Button>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              )}
            </CardBody>
          </Card>
        </VStack>
      </Box>
    </Box>
  );
};

export default NMAStrictDashboard;